/* src/components/GameScreen.js */
import React, { useEffect, useCallback, useState, useRef } from 'react';
// Import necessary hooks from react-router-dom
import { useNavigate, useBlocker } from 'react-router-dom';

// --- Contexts ---
import { useGame } from '../contexts/GameContext'; // Context for game state and actions
import { useProfile } from '../contexts/ProfileContext'; // Context for user profile data
import { useLoading as useLoadingContextHook } from '../contexts/LoadingContext'; // Context for global loading state

// --- Hooks ---
import useGameControls from './game/hooks/useGameControls'; // Hook for keyboard/button controls
import useGameResponses from './game/hooks/useGameResponses'; // Hook for handling match responses
import useGameFlow from './game/hooks/useGameFlow'; // Hook for post-game actions (retry, next, etc.)

// --- Components ---
import GameCountdown from './game/GameCountdown'; // Countdown screen before game starts
import GamePlayingScreen from './game/GamePlayingScreen'; // Main screen for active gameplay/pause
import GameResults from './game/GameResults'; // Screen displaying results after game ends
import LoadingScreen from './ui/LoadingScreen'; // Generic loading indicator component
import ConfirmationModal from './ui/ConfirmationModal'; // Reusable confirmation dialog
import FeedbackButton from './feedback/FeedbackButton'; // Feedback button component
// *** ADDED: Import GamePaused component ***
import GamePaused from './game/GamePaused'; // Standard pause overlay

/**
 * GameScreen Component
 * Orchestrates game phases (Countdown, Playing, Paused, Results).
 * Renders overlays like GamePaused and ConfirmationModal at the top level,
 * ensuring they cover the entire screen correctly. Passes down an
 * `isOverlayVisible` flag to child components like GamePlayingScreen
 * so they can dim their content when an overlay is active.
 */
const GameScreen = () => {
    // --- Basic Hooks ---
    const navigate = useNavigate(); // Function to programmatically navigate

    // --- Game Context ---
    // Destructure all needed state and actions from GameContext
    const {
        // State
        gameState, nBack, difficulty, gameResults, feedbackMessage, challengeState,
        currentTrial, sessionSettingsRef, sequence, totalTrials,
        pauseReason, // Reason game is paused ('manual', 'navigation_block', null)

        // Actions
        cancelPreparation, pauseGame, resumeGame, endGame, playAfterCountdown,
        registerResponse, resetContextResponseArrays, startGame,
        clearPauseReason, // Action to clear only the pause reason

        // Getters
        isMatch, getStimulusDuration, getInstantFeedbackEnabled, getVisualMatches,
        getAudioMatches, playFeedbackSound, logReplayEvent,

        // Refs
        isEndingRef // Flag to prevent race conditions during game end
    } = useGame();

    // --- Other Contexts ---
    const { activeProfile } = useProfile(); // Get the currently active user profile
    const { isLoading: isGlobalLoading } = useLoadingContextHook(); // Get global loading state

    // --- Component State ---
    // State to manage the initial loading of the screen component itself
    const [isScreenLoading, setIsScreenLoading] = useState(true);
    // Ref to store pending navigation when blocked
    const pendingTransition = useRef(null);

    // --- Game Logic Hooks ---
    // Hook managing user responses and feedback UI
    const {
        handlePositionResponse, handleAudioResponse, positionFeedback,
        audioFeedback, missedMatchTrial, visualResponseTrials,
        audioResponseTrials, resetResponses
    } = useGameResponses();

    // Hook managing keyboard controls and pause/resume button action
    const { handlePauseResume } = useGameControls({
        gameState,
        onPositionMatch: handlePositionResponse,
        onAudioMatch: handleAudioResponse,
        // Note: pause/resume actions are triggered via handlePauseResume,
        // which is passed to GameHeader. Direct calls here are not needed.
    });

    // Hook managing post-game actions
    const {
        handleRetryLevel, handleStartNextSession, handleGoToDashboard
    } = useGameFlow({
        startGame, resetContextResponseArrays, resetResponses, navigate,
        gameResults, activeProfile, sessionSettingsRef,
    });

    // --- Discard Threshold Logic ---
    // Calculates the minimum trial count required before a warning is shown
    const getDiscardThreshold = useCallback((totalTrialsPlanned) => {
        const validTotal = typeof totalTrialsPlanned === 'number' && totalTrialsPlanned > 0 ? totalTrialsPlanned : 0;
        return Math.max(1, Math.min(10, validTotal > 0 ? Math.ceil(validTotal * 0.5) : 10));
    }, []); // No dependencies, pure function

    // --- Helper to Check for Partial Score Warning ---
    // Determines if the "Leave Game?" confirmation modal should be shown
    const shouldWarnForPartialScore = useCallback(() => {
        const currentTrialFromState = currentTrial; // Read state directly
        const totalTrialsFromState = totalTrials; // Read state directly
        if (gameState !== 'playing' && gameState !== 'paused') return false;
        const trialsCompleted = currentTrialFromState + 1;
        const discardThreshold = getDiscardThreshold(totalTrialsFromState);
        return trialsCompleted >= discardThreshold;
    }, [currentTrial, totalTrials, getDiscardThreshold, gameState]); // Dependencies

    // --- Navigation Blocker Setup ---
    // Intercepts navigation attempts when the game is active
    const blocker = useBlocker(
        // Block if actively playing or paused (regardless of reason)
        gameState === 'playing' || gameState === 'paused'
    );

    // --- Effect to Handle Blocker State Changes ---
    // Runs when navigation is blocked or unblocked
    useEffect(() => {
        // If navigation is currently blocked:
        if (blocker && blocker.state === 'blocked') {
            pendingTransition.current = blocker; // Store the blocker object
            console.log('[Blocker] Navigation blocked.');

            // Check if a warning is needed based on progress
            if (shouldWarnForPartialScore()) {
                console.log('[Blocker] Pausing game to show confirmation modal.');
                // Pause the game specifically for navigation blocking
                if (typeof pauseGame === 'function') {
                    pauseGame('navigation_block'); // This pauseReason triggers the modal
                } else {
                    // Fallback if pause fails
                    console.error("[Blocker] pauseGame function not available!");
                    if (typeof endGame === 'function') endGame(); // End immediately
                    blocker.proceed(); // Allow navigation
                }
            } else {
                // No warning needed (e.g., early in the game), end the game immediately (likely discarding)
                console.log('[Blocker] Ending game immediately (no warning needed).');
                if (typeof endGame === 'function') {
                    endGame(); // Call standard endGame (will likely discard if few trials done)
                } else {
                    console.error("[Blocker] endGame function not available!");
                }
                // Immediately allow navigation to proceed after initiating endGame
                blocker.proceed();
                // Let the state change from endGame eventually unblock future navigation attempts naturally
            }
        } else if (blocker && blocker.state === 'unblocked') {
             // Clear ref if blocker becomes unblocked naturally (e.g., game ends normally)
            if (pendingTransition.current) {
                 console.log('[Blocker Effect] Blocker became unblocked, clearing pending transition ref.');
                 pendingTransition.current = null;
            }
        }

        // Cleanup function for the effect
        return () => {
            // Reset blocker if component unmounts while navigation is blocked
            if (pendingTransition.current && pendingTransition.current.state === 'blocked') {
                 console.log('[Blocker Cleanup] Resetting pending transition on unmount/re-run.');
                 pendingTransition.current.reset();
                 pendingTransition.current = null;
            }
        };
    // Only depend on the blocker object itself and the functions it calls
    }, [blocker, pauseGame, endGame, shouldWarnForPartialScore]);


    // --- Confirmation Modal Handlers ---
    // Called when the user confirms leaving ("Leave Game" button)
    const handleConfirmBackNavigation = useCallback(() => {
        console.log('[GameScreen] User confirmed leaving via modal.');
        // Call standard endGame to process results and potentially save partial score
        if (typeof endGame === 'function') {
            // Call default endGame (options default to { skipResultsProcessing: false })
            endGame();
        } else {
            console.error("[ConfirmBackNav] endGame function not available!");
        }
        // Allow the originally blocked navigation to proceed *immediately*
        if (pendingTransition.current) {
            pendingTransition.current.proceed();
            pendingTransition.current = null; // Clear the stored transition
        } else {
             console.warn("[ConfirmBackNav] No pending transition found to proceed with.");
        }
    }, [endGame]); // Dependency: endGame action

    // Called when the user cancels leaving ("Stay" button or closing the modal)
    const handleCancelBackNavigation = useCallback(() => {
        console.log('[GameScreen] User cancelled leaving via modal.');
        // 1. Hide the modal by clearing the specific pause reason
        if (typeof clearPauseReason === 'function') {
             clearPauseReason();
        } else {
            console.error("[CancelBackNav] clearPauseReason function not available!");
        }
        // 2. Cancel the blocked navigation attempt
        if (pendingTransition.current) {
            pendingTransition.current.reset();
            pendingTransition.current = null; // Clear the stored transition
        } else {
             console.warn("[CancelBackNav] No pending transition found to reset.");
        }
        // 3. Resume the game automatically since the user chose to stay
        if (typeof resumeGame === 'function') {
            console.log('[GameScreen] Resuming game after cancelling navigation leave.');
            resumeGame(); // Resume game from paused state
        } else {
             console.error("[CancelBackNav] resumeGame function not available!");
        }
    }, [clearPauseReason, resumeGame]); // Dependencies: clearPauseReason and resumeGame actions


    // --- Effect for Idle State Redirect ---
    // Redirects away from GameScreen if game state becomes idle unexpectedly
    useEffect(() => {
        const shouldRedirect =
            gameState === 'idle' &&
            !isGlobalLoading &&
            currentTrial === -1 &&
            !gameResults &&
            !challengeState.isActive;

        if (shouldRedirect) {
            console.log("[GameScreen Effect] State is idle and conditions met. Navigating to dashboard.");
            // Use setTimeout to avoid potential state update conflicts during render
            setTimeout(() => navigate('/dashboard', { replace: true }), 0);
        } else {
            // If not redirecting, ensure the screen loading state is false
            // This handles the initial load completion as well
            if (isScreenLoading) setIsScreenLoading(false);
        }
    }, [gameState, navigate, isGlobalLoading, currentTrial, gameResults, challengeState.isActive, isScreenLoading]); // Added isScreenLoading as dependency


    // --- Callbacks for Countdown Component ---
    // Triggered when countdown finishes
    const handleCountdownComplete = useCallback(() => {
        console.log("[GameScreen] Countdown visually complete signal received.");
        if (playAfterCountdown && typeof playAfterCountdown === 'function') {
            playAfterCountdown();
        } else {
            console.error("[GameScreen] playAfterCountdown missing or not a function!");
        }
    }, [playAfterCountdown]);

    // Triggered if user cancels countdown
    const handleCancelCountdown = useCallback(() => {
        console.log("[GameScreen] Countdown cancelled by user.");
        if (cancelPreparation && typeof cancelPreparation === 'function') {
            cancelPreparation();
        } else {
            console.error("[GameScreen] cancelPreparation missing or not a function!");
            navigate('/dashboard', { replace: true });
        }
    }, [cancelPreparation, navigate]);


    // --- Rendering Logic ---

    // Show loading screen during initial mount or transitions
    if (isScreenLoading) {
        return <LoadingScreen message="Initializing Game..." />;
    }

    // *** Determine Overlay Visibility ***
    // Should the standard "Game Paused" overlay be shown?
    const showStandardPauseOverlay = gameState === 'paused' && pauseReason !== 'navigation_block';
    // Should the "Leave Game?" confirmation modal be shown?
    const showConfirmationModal = pauseReason === 'navigation_block';
    // Is *any* overlay currently visible? (Used to dim underlying content)
    const isOverlayVisible = showStandardPauseOverlay || showConfirmationModal;

    // Determine the main content component based on the gameState
    let content;
    switch (gameState) {
        case 'preparing':
            const isChallengeSession = sessionSettingsRef.current?.isChallengeAttempt ?? false;
            // GameCountdown is rendered directly, already covers full screen
            content = (
                <GameCountdown
                    onComplete={handleCountdownComplete}
                    onCancel={handleCancelCountdown}
                    nBack={nBack}
                    difficulty={difficulty}
                    isChallenge={isChallengeSession}
                />
            );
            break;

        case 'playing':
        case 'paused':
            // Guard against rendering before essential game data is loaded
            if (currentTrial < 0 || totalTrials <= 0 || !Array.isArray(sequence) || sequence.length === 0) {
                 content = <LoadingScreen message="Starting Game..." />;
            } else {
                // Render the main gameplay screen component
                content = (
                    <GamePlayingScreen
                        // Pass necessary props from context and state
                        nBack={nBack} difficulty={difficulty} sequence={sequence}
                        currentTrial={currentTrial} totalTrials={totalTrials} gameState={gameState}
                        positionFeedback={positionFeedback} audioFeedback={audioFeedback}
                        missedMatchTrial={missedMatchTrial} visualResponseTrials={visualResponseTrials}
                        audioResponseTrials={audioResponseTrials}
                        handlePositionResponse={handlePositionResponse} handleAudioResponse={handleAudioResponse}
                        handlePauseResume={handlePauseResume} // Pass handler used by header button
                        // resumeGame & endGame are NOT passed here anymore; used directly by GamePaused below
                        getStimulusDuration={getStimulusDuration}
                        instantFeedbackIsEnabled={getInstantFeedbackEnabled ? getInstantFeedbackEnabled() : true}
                        renderControls={true} logReplayEvent={logReplayEvent}
                        // *** Pass the calculated overlay visibility flag ***
                        isOverlayVisible={isOverlayVisible}
                    />
                );
            }
            break;

        case 'results':
             // Guard against rendering results if data is missing
             if (!gameResults) {
                 console.error("[GameScreen Render] gameState 'results' but gameResults state is null. Navigating dashboard.");
                 setTimeout(() => navigate('/dashboard', { replace: true }), 0);
                 content = <LoadingScreen message="Error loading results..." />;
             } else {
                 // Render the results display component
                content = (
                    <GameResults
                        gameResults={gameResults}
                        feedbackMessage={feedbackMessage}
                        getFeatureIntroMessage={null} // Pass if needed
                        onDashboard={handleGoToDashboard}
                        onRetryLevel={handleRetryLevel}
                        onStartNextSession={handleStartNextSession}
                        resetResponses={resetResponses} // Combined reset function
                        onNavigate={navigate}
                    />
                );
             }
            break;

        case 'idle':
        default:
            // Default state, usually waiting for the redirect effect
            content = <LoadingScreen message="Loading dashboard..." />;
            break;
    }

    // --- Final Component Return ---
    // Render the determined main content, and conditionally render overlays on top
    return (
        <>
            {/* Render the main component determined by the switch statement */}
            {content}

            {/* *** Conditionally render GamePaused overlay HERE *** */}
            {/* Rendered outside 'content', allowing full screen coverage */}
            {/* Shown only when game is paused for reasons OTHER than navigation block */}
            {showStandardPauseOverlay && (
              <GamePaused
                onResume={resumeGame}     // Pass the resumeGame action from context
                onEndGame={endGame}     // Pass the endGame action from context
              />
            )}

            {/* Confirmation Modal for intercepted navigation attempts */}
            {/* Visibility controlled by checking the pauseReason */}
            <ConfirmationModal
                isOpen={showConfirmationModal} // Use calculated flag based on pauseReason
                onClose={handleCancelBackNavigation}    // User cancels -> reset blocker, clear reason, RESUME GAME
                onConfirm={handleConfirmBackNavigation} // User confirms -> end game (SAVE results), proceed blocker
                title="Leave Game Session?"
                confirmText="Leave Game"
                cancelText="Stay"
                confirmColor="red"
                isDestructive={true}
            >
                {/* Content displayed within the modal body */}
                <p className="text-sm text-gray-600">
                    Navigating away will end your current game session.
                </p>
                <p className="text-sm text-gray-600 mt-2">
                    You have completed enough trials to receive partial scoring if you leave now.
                </p>
                <p className="text-sm text-gray-600 mt-3 font-medium">
                    Are you sure you want to leave?
                </p>
            </ConfirmationModal>

            {/* Render the Feedback Button (position is fixed) */}
            <FeedbackButton />
        </>
    );
};

export default GameScreen;